import { dinero, type Dinero, toSnapshot } from 'dinero.js'

import { currencyOf, type CurrencyCode } from '#money/currency'
import { parseDecimal, rescaleExact } from '#money/decimal'
import { MoneyError } from '#money/errors'

/**
 * `Money` — a Dinero object **always held at its currency's own scale**.
 *
 * That last clause is the load-bearing one, and it is what makes invariant 05 structural
 * rather than remembered. Dinero can represent 1.005 USD by carrying a scale of 3; this
 * package never does. Every value that enters the domain passes through `fromNumeric` or
 * `roundForCurrency`, both of which land on the currency's exponent — so there is no such
 * thing here as an unrounded amount to multiply tax by. The rule "round the tax base to
 * currency precision **before** the multiply" is not a step a calculator can skip, because
 * the type it must pass to `taxOn` cannot hold the unrounded value.
 *
 * Dinero earns its place for the other half: it refuses arithmetic across currencies. That
 * is exactly the class of bug that produced v1's `amountPaid` defect, where an IDR gateway
 * amount was written to a USD invoice and nothing complained.
 */
export type Money = Dinero<number>

/** Re-exported so callers get the currency-checked operations from the same door as `Money`. */
export {
  add,
  subtract,
  multiply,
  allocate,
  compare,
  equal,
  greaterThan,
  greaterThanOrEqual,
  isNegative,
  isPositive,
  isZero,
  lessThan,
  lessThanOrEqual,
  maximum,
  minimum,
  toSnapshot,
} from 'dinero.js'

/**
 * Build a `Money` from minor units, checking the value is exactly representable.
 *
 * `number`, not `bigint`, because every amount this platform handles sits far inside the
 * safe range — a trillion rupiah is 1e12 against a 9e15 ceiling — and the `number` flavor
 * of Dinero is the one with currency support out of the box. The check is here so that
 * assumption fails loudly if it ever stops holding, instead of silently losing the low bits.
 */
export function fromMinorUnits(minor: bigint, code: CurrencyCode): Money {
  if (minor > BigInt(Number.MAX_SAFE_INTEGER) || minor < BigInt(Number.MIN_SAFE_INTEGER)) {
    throw new MoneyError(
      'amount_out_of_range',
      `${minor.toString()} minor units of ${code} is outside the range a number holds exactly.`,
      { currency: code, minorUnits: minor.toString() },
    )
  }
  return dinero({ amount: Number(minor), currency: currencyOf(code) })
}

/** The minor units behind a `Money`, as an exact integer. */
export function minorUnitsOf(value: Money): bigint {
  return BigInt(toSnapshot(value).amount)
}

/**
 * Decode a Drizzle `numeric` (string) into `Money`.
 *
 * **The column's scale is not the currency's scale.** One `numeric(N, 4)` column serves every
 * currency, so an IDR row reads back as `'1500000.0000'` — four decimals of padding the
 * column added, not money. Trailing zeros beyond the currency's exponent are therefore
 * dropped; a **non-zero** digit beyond it is refused, because that is a value the ledger
 * cannot represent and quietly truncating it is how a reconciliation ends up short with
 * nothing in the log saying where.
 */
export function fromNumeric(value: string, code: string): Money {
  const currency = currencyOf(code)
  const decimal = parseDecimal(value, { currency: currency.code })
  const minor = rescaleExact(decimal, currency.exponent)

  if (minor === undefined) {
    throw new MoneyError(
      'sub_unit_precision',
      `${value} carries a sub-unit ${currency.code} cannot represent (it has ${currency.exponent} decimal places).`,
      { value, currency: currency.code, exponent: currency.exponent },
    )
  }

  return fromMinorUnits(minor, currency.code)
}

/** Render minor units as a decimal string with `scale` places. */
export function decimalFromMinor(minor: bigint, scale: number): string {
  const negative = minor < 0n
  const digits = (negative ? -minor : minor).toString().padStart(scale + 1, '0')
  const whole = digits.slice(0, digits.length - scale)
  const fraction = scale === 0 ? '' : `.${digits.slice(digits.length - scale)}`
  return `${negative ? '-' : ''}${whole}${fraction}`
}

/**
 * Encode `Money` back to a string for a `numeric` column.
 *
 * A string, never a `number`: `numeric` is exact in Postgres and arrives as a string
 * precisely so it stays exact. Handing the driver a float re-introduces the drift the
 * column type exists to prevent.
 */
export function toNumeric(value: Money): string {
  const { amount, scale } = toSnapshot(value)
  return decimalFromMinor(BigInt(amount), scale)
}

/**
 * The **only** place money becomes a JS number, and it is one-way (stack §11).
 *
 * Response schemas emit `z.number()` for monetary fields, so the DTO boundary needs this.
 * Nothing reads it back: a number that came from here goes onto the wire and no further.
 */
export function toResponseNumber(value: Money): number {
  return Number(toNumeric(value))
}

export function zero(code: CurrencyCode): Money {
  return fromMinorUnits(0n, code)
}
